<script lang="ts" setup>
import type LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useDeactivateFirmware } from "~/modules/firmware/composables/useDeactivateFirmware";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import { FirmwareStatus } from "~/modules/firmware/models/FirmwareStatus.enum";
import ILxcLightSlash from "~icons/lxc-light/slash";

const props = defineProps<{
  businessUnitCode: number;
  firmware: Firmware;
}>();
const emit = defineEmits(["change"]);

const { t } = useI18n();

const [deactivateConfirmDialogVisible, toggleDeactivateConfirmVisible] =
  useToggle();

const { isLoading, error, deactivateFirmware } = useDeactivateFirmware({
  businessUnitCode: props.businessUnitCode,
});

const buttonVisible = computed<boolean>(
  () => props.firmware?.status === FirmwareStatus.ACTIVATED,
);

const onDeactivateFirmware = async () => {
  await deactivateFirmware(props.firmware.uuid);

  if (error.value) {
    (error.value as LxcError).notify(NotificationKey.saveError);
  } else {
    const newFirmware = { ...props.firmware };
    newFirmware.status = FirmwareStatus.DEACTIVATED;
    showNotificationSuccess(t(NotificationKey.saveSuccess));
    emit("change", newFirmware);
  }

  toggleDeactivateConfirmVisible(false);
};

const confirmDeactivateFirmware = async (event: MouseEvent) => {
  event?.preventDefault();
  event?.stopPropagation();

  if (props.firmware.uuid) {
    toggleDeactivateConfirmVisible();
  }
};
</script>

<template>
  <lxc-button
    v-if="buttonVisible"
    html-type="button"
    type="borderless"
    :disabled="isLoading"
    :icon="!isLoading ? ILxcLightSlash : undefined"
    :title="t('firmware.actions.deactivate.label')"
    class="invisible"
    @click.prevent="confirmDeactivateFirmware"
  >
    <lxc-loader v-if="isLoading" />
  </lxc-button>

  <confirm-modal
    :is-dialog-visible="deactivateConfirmDialogVisible"
    :title="t('firmware.actions.deactivate.label')"
    :description="
      t('firmware.actions.deactivate.description', {
        name: props.firmware.name,
      })
    "
    :is-loading="isLoading"
    @cancel="toggleDeactivateConfirmVisible"
    @confirm="onDeactivateFirmware"
  />
</template>
<style lang="scss" scoped>
button {
  :deep(svg) {
    height: 20px;
    width: 20px;
  }
}
</style>
